// Lightweight device-type heuristics for the Tx Presenter "Present on Tablet"
// hand-off. There's no perfect "is this a tablet" signal, so we detect a
// touch-PRIMARY device (tablet/phone) and bias to FALSE (treat as desktop) on
// any error — a desktop user must always be able to reach the QR hand-off,
// whereas an extra button on a tablet is harmless.

/**
 * True when the device's primary pointer is a finger (tablet/phone).
 *
 * `pointer: coarse` reflects the PRIMARY pointing device, so a laptop with a
 * trackpad (even a touchscreen 2-in-1 in laptop mode) reports `fine` and is
 * treated as desktop. The `maxTouchPoints > 1` guard also catches iPads even
 * when iPadOS Safari masquerades as "Macintosh" (it still reports 5 touch
 * points and a coarse pointer).
 */
export function isTouchPrimaryDevice() {
  try {
    const coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    return coarse && (navigator.maxTouchPoints || 0) > 1;
  } catch (e) {
    return false;
  }
}

/**
 * True when there is ANY touch screen to point a camera with — deliberately looser than
 * isTouchPrimaryDevice. Three signals, any one wins: `maxTouchPoints` is the steadiest (iPadOS
 * Safari in its default desktop-site mode still reports 5 while its UA claims to be a Mac), the
 * coarse-pointer query covers Android, and `ontouchstart` is the legacy fallback.
 *
 * Used by PatientPhotoAvatar ("Take photo" row) and the header search's camera shortcut, so the
 * shortcut appears exactly where the sheet it opens offers "Take photo".
 */
export function hasTouchScreen() {
  try {
    if (typeof window === 'undefined') return false;
    return (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
      || !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
      || 'ontouchstart' in window;
  } catch (e) {
    return false;
  }
}

export default { isTouchPrimaryDevice, hasTouchScreen };
